/**
 * One chat row of a built-in tab, shared by the Chats page's Workspace tabs
 * (builtinTabs.tsx) and Workspace's tabs view: same avatar, badge chip, time,
 * preview and hover action. The caller draws the avatar (each page loads
 * OnlyFans media its own way) and says which row is open.
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState, type PointerEvent, type ReactNode } from 'react'
import { FeatherIcon } from '../components/FeatherIcon'

/** Rows drawn at first; more are added as the end of the list comes near (long lists stay light). */
export const FIRST_ROWS = 30
export const MORE_ROWS = 30

export interface RowBadge {
  icon: string
  label: string
  amount?: string
  /** oklch hue of the badge's meaning colour (Activity kinds). */
  hue?: number
  tone?: 'accent' | 'warn' | 'muted'
}

export interface RowView {
  key: string
  fanId: string
  name: string
  /** Drawn inside the row's avatar slot. */
  avatar: ReactNode
  /**
   * Changes whenever the avatar (or `chips`) would draw differently. With it, a
   * row redraws only when something it shows changed (not on every new element).
   */
  avatarKey?: string
  /** Drawn before the badge on the row's first line (e.g. buying power). */
  chips?: ReactNode
  /** The model, muted after the fan's name (rows that mix Accounts). */
  model?: string | null
  badge?: RowBadge
  preview: string
  time: string
  dim?: number
  action?: { icon: string; label: string; run: () => void }
  /** A heading drawn above this row (e.g. "Fan replied · 3"). */
  group?: string | null
}

export interface RowDrag {
  /** Pointer pressed on a row: the page's chat drag takes it from here. */
  start: (row: RowView, e: PointerEvent) => void
  /** The row being dragged right now (dimmed). */
  draggingKey: string | null
}

interface RowsProps {
  rows: RowView[]
  isActive: (row: RowView) => boolean
  onOpen: (row: RowView) => void
  loading: boolean
  empty: ReactNode
  drag?: RowDrag
}

export function GroupHead({ label }: { label: string }) {
  return <div className="chat-tab-group-head">{label}</div>
}

export function TabEmpty({ icon, title, text, children }: { icon: string; title: string; text?: string; children?: ReactNode }) {
  return (
    <div className="chat-list-empty">
      <div className="chat-list-empty-icon">
        <FeatherIcon name={icon} size={22} />
      </div>
      <div className="chat-list-empty-title">{title}</div>
      {text && <div className="chat-tab-empty-text">{text}</div>}
      {children}
    </div>
  )
}

export function TabRows({ rows, isActive, onOpen, loading, empty, drag }: RowsProps) {
  const [limit, setLimit] = useState(FIRST_ROWS)
  // Handlers stay the same between renders (memoised rows keep them) and act on the latest row.
  const latest = useRef({ rows: new Map<string, RowView>(), onOpen, drag })
  latest.current = { rows: new Map(rows.map((r) => [r.key, r])), onOpen, drag }
  const handlers = useMemo<RowHandlers>(
    () => ({
      open: (key) => {
        const r = latest.current.rows.get(key)
        if (r) latest.current.onOpen(r)
      },
      start: (key, e) => {
        const r = latest.current.rows.get(key)
        if (r) latest.current.drag?.start(r, e)
      },
      act: (key) => latest.current.rows.get(key)?.action?.run(),
    }),
    [],
  )
  const more = useCallback(() => setLimit((n) => n + MORE_ROWS), [])
  if (!rows.length)
    return loading ? (
      <div className="chat-list-more-loader" aria-busy="true">
        <span className="chat-list-spinner" />
      </div>
    ) : (
      <>{empty}</>
    )
  const drawn = rows.length > limit ? rows.slice(0, limit) : rows
  return (
    <>
      {drawn.map((r) => [
        r.group ? <GroupHead key={`g:${r.key}`} label={r.group} /> : null,
        <MemoRow key={r.key} row={r} active={isActive(r)} dragging={drag?.draggingKey === r.key} draggable={!!drag} h={handlers} />,
      ])}
      {rows.length > limit ? (
        <MoreRows onNear={more} token={limit} />
      ) : (
        loading && (
          <div className="chat-list-more-loader" aria-busy="true">
            <span className="chat-list-spinner" />
          </div>
        )
      )}
    </>
  )
}

interface RowHandlers {
  open: (key: string) => void
  start: (key: string, e: PointerEvent) => void
  act: (key: string) => void
}

/** Asks for more rows when it comes within a screenful of the list's scroll box. */
function MoreRows({ onNear, token }: { onNear: () => void; token: number }) {
  const ref = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    let root: HTMLElement | null = el.parentElement
    while (root && !/(auto|scroll)/.test(getComputedStyle(root).overflowY)) root = root.parentElement
    const io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && onNear(), { root, rootMargin: '0px 0px 600px 0px' })
    io.observe(el)
    return () => io.disconnect()
  }, [onNear, token])
  return <div ref={ref} className="chat-tab-more" aria-hidden="true" style={{ height: 1 }} />
}

function sameBadge(a: RowBadge | undefined, b: RowBadge | undefined) {
  return a === b || (!!a && !!b && a.icon === b.icon && a.label === b.label && a.amount === b.amount && a.hue === b.hue && a.tone === b.tone)
}

/** Same on screen: rows are rebuilt on every list render, so compare what they show. */
function sameRow(a: RowView, b: RowView) {
  return (
    a.key === b.key &&
    a.fanId === b.fanId &&
    a.name === b.name &&
    a.model === b.model &&
    a.preview === b.preview &&
    a.time === b.time &&
    a.dim === b.dim &&
    (a.avatarKey != null ? a.avatarKey === b.avatarKey : a.avatar === b.avatar && a.chips === b.chips) &&
    sameBadge(a.badge, b.badge) &&
    a.action?.icon === b.action?.icon &&
    a.action?.label === b.action?.label
  )
}

interface MemoRowProps {
  row: RowView
  active: boolean
  dragging: boolean
  draggable: boolean
  h: RowHandlers
}

const MemoRow = memo(
  function MemoRow({ row, active, dragging, draggable, h }: MemoRowProps) {
    return (
      <TabRow
        row={row}
        active={active}
        dragging={dragging}
        onOpen={() => h.open(row.key)}
        onPointerDown={draggable ? (e) => h.start(row.key, e) : undefined}
        onAction={row.action ? () => h.act(row.key) : undefined}
      />
    )
  },
  (a, b) => a.active === b.active && a.dragging === b.dragging && a.draggable === b.draggable && a.h === b.h && sameRow(a.row, b.row),
)

interface TabRowProps {
  row: RowView
  active: boolean
  dragging: boolean
  onOpen: () => void
  onPointerDown?: (e: PointerEvent) => void
  onAction?: () => void
}

export function TabRow({ row, active, dragging, onOpen, onPointerDown, onAction }: TabRowProps) {
  const badgeColour = row.badge?.hue != null ? `oklch(0.8 0.1 ${row.badge.hue})` : undefined
  const opacity = dragging ? 0.4 : row.dim != null && row.dim < 1 ? row.dim : undefined
  return (
    <div role="listitem" className="chat-item-li chat-tab-li" data-chat-key={row.key} style={opacity != null ? { opacity } : undefined}>
      <button
        type="button"
        className={`chat-item ${active ? 'active' : ''}`}
        data-chat-fan-id={row.fanId}
        onPointerDown={onPointerDown}
        onClick={onOpen}
      >
        <span className="chat-avatar-wrap">{row.avatar}</span>
        <span className="chat-info">
          <span className="chat-info-top">
            {row.chips}
            {row.badge && (
              <span className={`chat-tab-badge ${row.badge.tone ? `is-${row.badge.tone}` : ''}`} style={badgeColour ? { color: badgeColour, borderColor: badgeColour } : undefined}>
                <FeatherIcon name={row.badge.icon} size={11} />
                {row.badge.label}
                {row.badge.amount && <b>{row.badge.amount}</b>}
              </span>
            )}
            <span className="chat-meta">
              <span className="chat-meta-row">
                <span className="chat-time">{row.time}</span>
              </span>
            </span>
          </span>
          <span className="chat-title-line">
            <span className="chat-name">{row.name}</span>
            {row.model && <span className="chat-handle chat-tab-model">· {row.model}</span>}
          </span>
          <span className="chat-preview" title={row.preview}>
            {row.preview || '—'}
          </span>
        </span>
      </button>
      {row.action && (
        <button type="button" className="chat-tab-action" aria-label={`${row.action.label} · ${row.name}`} title={row.action.label} onClick={onAction}>
          <FeatherIcon name={row.action.icon} size={13} />
        </button>
      )}
    </div>
  )
}
